import { Metadata } from "@/types/metadata"
import { useEffect, useState, useCallback, useMemo } from "react"
import { MemoryCache } from "@/utils/cache"
import { EventService } from "@/services/events"
import { useMetadata } from "@/hooks/use-metadata"
import { EventFilters } from "@/types/filters"

const eventProvincesCache = new MemoryCache<Array<Metadata>>({
    disableInDev: false
})

type State = {
    data: Array<Metadata>;
    loading: boolean;
}

const useEventProvinces = (lang: string, filters?: EventFilters) => {
    const eventService = useMemo(() => EventService.getInstance(), [])
    const { getStatesByCountry } = useMetadata()

    const [state, setState] = useState<State>({
        loading: true,
        data: []
    })

    const cacheKey = useMemo(() => {
        if (!filters || Object.keys(filters).length === 0) {
            return `event_provinces_${lang}_no_filters`
        }

        const filterParts = []
        if (filters.pattern) filterParts.push(`pattern:${filters.pattern}`)
        if (filters.sport) filterParts.push(`sport:${filters.sport}`)
        if (filters.categories) filterParts.push(`categories:${filters.categories.join(',')}`)
        if (filters.country) filterParts.push(`country:${filters.country}`)
        if (filters.dateFilter) filterParts.push(`date:${filters.dateFilter.type}`)

        const filtersKey = filterParts.sort().join('|')
        return `event_provinces_${lang}_${filtersKey}`
    }, [lang, filters])

    const handleGetEventProvinces = useCallback(async () => {
        try {
            if (!filters?.country) {
                setState({
                    loading: false,
                    data: []
                })
                return
            }

            // Check memory cache first (instant, no API call needed)
            const cachedData = eventProvincesCache.get(cacheKey)
            if (cachedData) {
                setState({
                    loading: false,
                    data: cachedData
                })
                return
            }

            // Defer the expensive provinces query by 1.5s so the events query
            // gets priority on CosmosDB RU budget (provinces groupBy costs ~1200-2700 RU)
            await new Promise(resolve => setTimeout(resolve, 1500))

            const provincesResponse = await eventProvincesCache.getOrSet(cacheKey, async () => {
                let filtersForBackend = { ...filters };

                const response = await eventService.getAvailableProvinces(filtersForBackend)

                if (response.error) {
                    return null
                }

                const provinceIds: number[] = response.data.items?.map((item: any) => item.provinceId) || []

                const countryId = filters.country
                const allProvincesResponse = await getStatesByCountry(lang, countryId)

                if (allProvincesResponse.error || !allProvincesResponse.data) {
                    return []
                }

                const allProvinces = allProvincesResponse.data.items || []
                const filteredProvinces = allProvinces.filter(p => provinceIds.includes(p.id))

                return filteredProvinces.sort((a, b) => a.value.localeCompare(b.value, lang))
            })

            if (provincesResponse) {
                setState({
                    loading: false,
                    data: provincesResponse || []
                })
            }
        }
        catch (e) {
            setState({
                loading: false,
                data: []
            })
        }
    }, [cacheKey, filters, lang, eventService, getStatesByCountry])

    useEffect(() => {
        // Solo mostrar loading si no tenemos datos en caché
        const cachedData = eventProvincesCache.get(cacheKey)
        if (!cachedData && filters?.country) {
            setState(prevState => ({
                loading: true,
                data: prevState.data
            }))
        }
        handleGetEventProvinces()
    }, [handleGetEventProvinces, cacheKey, filters?.country])

    return state
}

export default useEventProvinces